import {getRoleFromPost} from "@hosttools/core/shared/getRoleFromPost";
import {Channel} from "@hosttools/core/shared/model/channel";

import type {Message} from "../../../models/message";
import type {ReservationPost, ReservationSource} from "../../../models/reservation";
import type {Timeline} from "../../../models/timeline";

export interface Post {
    id: string;
    date: string;
    post:
        | {
              type: "timeline";
              data: Timeline;
              role: "host";
          }
        | {
              type: "message";
              data: Message;
              role: "host";
          }
        | {
              type: "post";
              data: ReservationPost;
              role: ReservationPost["role"];
          };
}

export const postFillerPrefix = "addedAt-";
export const base64ImagePrefix = "data:image/png;base64,";

export const buildPosts = (
    reservationID: string,
    source: ReservationSource,
    baseUrl: string,
    posts: ReservationPost[]
) => {
    return posts.map<Post>(post => {
        if ((source === Channel.Booking || source === Channel.HomeAway) && post.attachments) {
            const attachments = post.attachments.reduce((arr, elem) => {
                // Do nothing if url is existing
                if (elem.url) {
                    arr.push(elem);
                    return arr;
                }

                // treat `publicId` as `attachmentID`
                const attachmentID = elem.publicId;
                arr.push({
                    _id: "", // not used for now
                    url: `${baseUrl}/getAttachment/${reservationID}/${attachmentID}`,
                    publicId: attachmentID
                });
                return arr;
            }, [] as Required<ReservationPost>["attachments"]);

            return {
                id: post._id,
                date: post.sentTimestamp,
                post: {
                    type: "post",
                    data: {...post, attachments},
                    role: getRoleFromPost(post)
                }
            };
        }

        return {
            id: post._id,
            date: post.sentTimestamp,
            post: {
                type: "post",
                data: {
                    ...post,
                    ...(post.attachments && {
                        attachments: post.attachments.filter(elem => elem.url)
                    })
                },
                role: getRoleFromPost(post)
            }
        };
    });
};

export function sortPosts<T extends {sentTimestamp: string}>(posts?: T[]): T[] {
    return posts
        ? posts.sort((a, b) => {
              const dateA = new Date(a.sentTimestamp).getTime();
              const dateB = new Date(b.sentTimestamp).getTime();

              return dateA > dateB ? -1 : 1;
          })
        : [];
}

export const filterOutFillerMessages = (items: Post[]) =>
    items.filter(elem => !elem.id.startsWith(postFillerPrefix));

export const filterDuplicatedItems = <T extends {id: string}>(prevOnes: T[], addedOnes: T[]) => {
    const prevIDs = prevOnes.map(elem => elem.id);
    return addedOnes.filter(addedOne => {
        return !prevIDs.includes(addedOne.id);
    });
};
